// Fuzzy matching between free-text student entries and case findings.
//
// Three tiers, cheapest first:
//   1. alias hit on a whole-word boundary ("mi" never matches "vomiting")
//   2. normalized bidirectional containment
//   3. significant-token overlap: opt-in via { tokens: true }
//
// Tier 3 exists because students phrase questions freely ("listen to the
// heart" vs the finding "Heart sounds"). It is deliberately NOT used for
// diagnosis matching, where it would equate "pulmonary edema" with
// "pulmonary embolism".

// Hyphens and slashes are word breaks ("ACE-inhibitor" is "ace inhibitor",
// "92/58" is two numbers); other punctuation is dropped.
export const normalize = (s) =>
  (s || '').toLowerCase().replace(/[-–—/]+/g, ' ').replace(/[^\p{L}\p{N}\s]/gu, '').replace(/\s+/g, ' ').trim();

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const matchesAliases = (text, aliases = []) => {
  const t = (text || '').toLowerCase();
  return aliases.some((alias) =>
    new RegExp(`(^|[^\\p{L}\\p{N}])${escapeRegExp(alias.toLowerCase())}([^\\p{L}\\p{N}]|$)`, 'u').test(t)
  );
};

// Crude singularization so "legs"/"leg" and "sounds"/"sound" unify.
const stem = (t) => (t.length > 3 && t.endsWith('s') ? t.slice(0, -1) : t);

// Grammar words plus clinical filler that carries no diagnostic signal, 
// these must never be the sole reason two phrases match.
const STOPWORDS = new Set([
  'the', 'and', 'or', 'of', 'to', 'in', 'on', 'for', 'with', 'any', 'all', 'a', 'an',
  'is', 'are', 'was', 'were', 'do', 'doe', 'did', 'can', 'you', 'your', 'their', 'them',
  'have', 'ha', 'had', 'get', 'got', 'been', 'from', 'that', 'thi', 'it', 'if',
  'what', 'when', 'where', 'why', 'how', 'which', 'who',
  'ask', 'about', 'check', 'exam', 'examine', 'examination', 'assess', 'look', 'listen',
  'inspect', 'perform', 'please', 'patient', 'pt', 'history', 'hx', 'sign', 'test',
  'question', 'like', 'other', 'some', 'more', 'also', 'well', 'take',
  // Temporal / quantity filler, these words appear in almost any clinical
  // question ("...before?", "how long...", "any recent...") and must never
  // carry a match on their own.
  'before', 'after', 'again', 'ever', 'never', 'first', 'last', 'time', 'long',
  'recent', 'recently', 'much', 'many', 'often', 'new', 'old', 'now', 'today',
  // Investigation filler, lab names share these constantly ("renal function"
  // vs "liver function", "blood count" vs "blood gas"). Precise aliases carry
  // the match for tests; these words must not.
  'blood', 'function', 'panel', 'level', 'screen', 'study', 'work', 'result', 'count',
  // Symptom filler, "pain" alone linked "chest pain" to a finding whose only
  // overlap was the alias "joint pain". The body part carries the meaning.
  'pain', 'symptom', 'problem', 'issue', 'complaint',
]);

const tokenize = (s) =>
  normalize(s)
    .split(' ')
    .map(stem)
    .filter((t) => t.length >= 3 && !STOPWORDS.has(t));

// finding: { text, aliases?: string[] }
export const fuzzyMatch = (entryText, finding, { tokens = false } = {}) => {
  const entry = normalize(entryText);
  if (!entry) return false;
  if (matchesAliases(entry, finding.aliases)) return true;

  const target = normalize(finding.text);
  if (!target) return false;
  if ((entry.length >= 4 && target.includes(entry)) || (target.length >= 4 && entry.includes(target))) return true;

  if (!tokens) return false;
  const entryTokens = tokenize(entry);
  if (!entryTokens.length) return false;
  const findingTokens = new Set([
    ...tokenize(finding.text),
    ...(finding.aliases || []).flatMap((a) => tokenize(a)),
  ]);
  return entryTokens.some((t) => findingTokens.has(t));
};
